import { createAsyncAction, createAction } from "typesafe-actions";
import { CoreValue, Recognition } from "@shared/models";
import { PaginatedResponse, WithCallback } from "@shared/interfaces";
import { RecognitionFilter } from "@shared/interfaces/RecognitionFilter.interface";
import {
  BulkApproveDto,
  BulkApproveResult,
  CreateRecognitionDto,
  CreateRecognitionSuccess,
  RecognitionCounts,
  UpdateRecognitionDto,
} from "@containers/Recognition/interfaces";

import { RecognitionActionTypes } from "./constants";

export const getReceivedRecognitions = createAsyncAction(
  RecognitionActionTypes.GET_RECEIVED_RECOGNITIONS,
  RecognitionActionTypes.GET_RECEIVED_RECOGNITIONS_SUCCESS,
  RecognitionActionTypes.GET_RECEIVED_RECOGNITIONS_FAILURE,
)<RecognitionFilter, PaginatedResponse<Recognition>, Error>();

export const getSentRecognitions = createAsyncAction(
  RecognitionActionTypes.GET_SENT_RECOGNITIONS,
  RecognitionActionTypes.GET_SENT_RECOGNITIONS_SUCCESS,
  RecognitionActionTypes.GET_SENT_RECOGNITIONS_FAILURE,
)<RecognitionFilter, PaginatedResponse<Recognition>, Error>();

export const getReviewRecognitions = createAsyncAction(
  RecognitionActionTypes.GET_REVIEW_RECOGNITIONS,
  RecognitionActionTypes.GET_REVIEW_RECOGNITIONS_SUCCESS,
  RecognitionActionTypes.GET_REVIEW_RECOGNITIONS_FAILURE,
)<RecognitionFilter, PaginatedResponse<Recognition>, Error>();

export const getRecognition = createAsyncAction(
  RecognitionActionTypes.GET_RECOGNITION,
  RecognitionActionTypes.GET_RECOGNITION_SUCCESS,
  RecognitionActionTypes.GET_RECOGNITION_FAILURE,
)<number, Recognition | null, Error>();

export const createRecognition = createAsyncAction(
  RecognitionActionTypes.CREATE_RECOGNITION,
  RecognitionActionTypes.CREATE_RECOGNITION_SUCCESS,
  RecognitionActionTypes.CREATE_RECOGNITION_FAILURE,
)<WithCallback<CreateRecognitionDto>, CreateRecognitionSuccess, Error>();

// 923-core-context context/engineering/spark-rewards/phase-53-decline-reason-and-decision-buttons.md PR3: an optional `cb`, matching createRecognition's
// own WithCallback precedent above -- DeclineRecognitionContainer needs a success-only signal to hide
// itself, the same shape DenyRedemptionRequestContainer's own denyRedemptionRequest.request already
// uses. Every existing caller (approve, and any plain field edit) keeps dispatching with no `cb` at all
// -- WithCallback's own `cb?:` makes that a no-op change for them.
export const updateRecognition = createAsyncAction(
  RecognitionActionTypes.UPDATE_RECOGNITION,
  RecognitionActionTypes.UPDATE_RECOGNITION_SUCCESS,
  RecognitionActionTypes.UPDATE_RECOGNITION_FAILURE,
)<WithCallback<UpdateRecognitionDto>, CreateRecognitionSuccess, Error>();

export const getCoreValues = createAsyncAction(
  RecognitionActionTypes.GET_CORE_VALUES,
  RecognitionActionTypes.GET_CORE_VALUES_SUCCESS,
  RecognitionActionTypes.GET_CORE_VALUES_FAILURE,
)<undefined, CoreValue[], Error>();

export const bulkApproveRecognitions = createAsyncAction(
  RecognitionActionTypes.BULK_APPROVE_RECOGNITIONS,
  RecognitionActionTypes.BULK_APPROVE_RECOGNITIONS_SUCCESS,
  RecognitionActionTypes.BULK_APPROVE_RECOGNITIONS_FAILURE,
)<WithCallback<BulkApproveDto>, BulkApproveResult, Error>();

// D6: no payload on any arm, matching `bootstrap`'s own silent-poll precedent
// (Auth/store/actions.tsx:34-38) -- errorHandlerMiddleware (store/store.tsx:29) only auto-toasts a
// FAILURE action whose payload is truthy, so a bare `.failure()` here is what keeps a poll failing
// every 30s (a dropped connection, an expired session) from toasting an error every 30s forever. The
// failure slot is typed `undefined`, not `unknown` -- typing it `unknown` silently resolves to `any`
// under this repo's typesafe-actions (see store/sparks/actions.tsx's own file-level warning).
export const getPendingCount = createAsyncAction(
  RecognitionActionTypes.GET_PENDING_COUNT,
  RecognitionActionTypes.GET_PENDING_COUNT_SUCCESS,
  RecognitionActionTypes.GET_PENDING_COUNT_FAILURE,
)<undefined, { count: number }, undefined>();

// page-header-promotion-plan.md: the page header's combined received+sent count badge. Payload is the
// VIEWER's own id -- api.getRecognitions (reused by the saga below) filters by an explicit
// `user_id`/`user_created_by_id` query param rather than inferring the caller from the auth token
// (RecognitionListContainer.tsx's own `user_id: user?.id` / `user_created_by_id: user?.id` seeding is
// the same convention), unlike getPendingCount's payload-less request. Failure carries a real `Error`
// (not the bare `undefined` getPendingCount uses) -- this is a one-time fetch on mount, not a silent
// 30s background poll, so it does not need that action's toast-suppression.
export const getRecognitionCounts = createAsyncAction(
  RecognitionActionTypes.GET_RECOGNITION_COUNTS,
  RecognitionActionTypes.GET_RECOGNITION_COUNTS_SUCCESS,
  RecognitionActionTypes.GET_RECOGNITION_COUNTS_FAILURE,
)<number, RecognitionCounts, Error>();

export const setReceivedFilter = createAction(RecognitionActionTypes.SET_RECEIVED_FILTER)<RecognitionFilter | null>();
export const setSentFilter = createAction(RecognitionActionTypes.SET_SENT_FILTER)<RecognitionFilter | null>();
export const setReviewFilter = createAction(RecognitionActionTypes.SET_REVIEW_FILTER)<RecognitionFilter | null>();

export const clearRecognitions = createAction(RecognitionActionTypes.CLEAR_RECOGNITIONS)();
export const clearCoreValues = createAction(RecognitionActionTypes.CLEAR_CORE_VALUES)();
export const clearReviewRecognitions = createAction(RecognitionActionTypes.CLEAR_REVIEW_RECOGNITIONS)();
